<template>
  <div v-if="isOpen" class="modal-overlay" @click="handleClose">
    <div class="modal-content" @click.stop>
      <h2 class="modal-title">Add Streaming Server</h2>
      <div class="line-divider"></div>

      <form @submit.prevent="handleSubmit" class="modal-form">
        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
        <p v-if="successMessage" class="success-message">
          {{ successMessage }}
        </p>

        <div class="form-group">
          <label for="server-name">Server Name *</label>
          <input
            type="text"
            id="server-name"
            v-model="formData.name"
            placeholder="US West Server"
            required
          />
        </div>

        <div class="form-group">
          <label for="ip-address">IP Address *</label>
          <input
            type="text"
            id="ip-address"
            v-model="formData.ipAddress"
            placeholder="34.214.125.9"
            required
            pattern="^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$"
          />
          <small class="form-hint">Enter the server IP address</small>
        </div>

        <div class="form-row">
          <div class="form-group form-group-half">
            <label for="rtmp-port">RTMP Port *</label>
            <input
              type="number"
              id="rtmp-port"
              v-model.number="formData.rtmpPort"
              placeholder="1935"
              required
              min="1"
              max="65535"
            />
          </div>

          <div class="form-group form-group-half">
            <label for="webrtc-port">WebRTC Port *</label>
            <input
              type="number"
              id="webrtc-port"
              v-model.number="formData.webrtcPort"
              placeholder="8443"
              required
              min="1"
              max="65535"
            />
          </div>
        </div>

        <div class="form-group">
          <label for="webrtc-domain">WebRTC Domain *</label>
          <input
            type="text"
            id="webrtc-domain"
            v-model="formData.webrtcDomain"
            placeholder="indiastream.skyhost.ai"
          />
          <small class="form-hint"
            >Leave empty to use IP address for WebRTC</small
          >
        </div>

        <div class="form-group">
          <label for="stream-keys">Stream Keys *</label>
          <textarea
            id="stream-keys"
            v-model="streamKeysInput"
            placeholder="stream1, stream2, stream3, stream4, stream5"
            rows="3"
            required
          ></textarea>
          <small class="form-hint"
            >Enter comma-separated stream keys (default: stream1-stream5)</small
          >
        </div>

        <div class="button-group">
          <button type="submit" class="modal-submit" :disabled="isLoading">
            <div class="button-content">
              <div v-if="isLoading" class="spinner"></div>
              <span>{{ isLoading ? "Creating..." : "Create Server" }}</span>
            </div>
          </button>
          <button
            type="button"
            class="modal-cancel"
            @click="handleClose"
            :disabled="isLoading"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { createStreamingServer } from "@/services/streamingService";

export default {
  name: "AddServerModal",
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      formData: {
        name: "",
        ipAddress: "",
        rtmpPort: 1935,
        webrtcPort: 8443,
        webrtcDomain: "",
      },
      streamKeysInput: "stream1, stream2, stream3, stream4, stream5",
      isLoading: false,
      errorMessage: "",
      successMessage: "",
    };
  },
  computed: {
    modalWidth() {
      return isMobile() ? "90%" : "600px";
    },
  },
  methods: {
    handleClose() {
      if (!this.isLoading) {
        this.clearForm();
        this.$emit("close");
      }
    },
    clearForm() {
      this.formData = {
        name: "",
        ipAddress: "",
        rtmpPort: 1935,
        webrtcPort: 8443,
        webrtcDomain: "",
      };
      this.streamKeysInput = "stream1, stream2, stream3, stream4, stream5";
      this.isLoading = false;
      this.errorMessage = "";
      this.successMessage = "";
    },
    parseStreamKeys(input) {
      return input
        .split(",")
        .map((key) => key.trim())
        .filter((key) => key.length > 0);
    },
    async handleSubmit() {
      this.errorMessage = "";
      this.successMessage = "";
      this.isLoading = true;

      try {
        // Parse stream keys
        const availableKeys = this.parseStreamKeys(this.streamKeysInput);
        if (availableKeys.length === 0) {
          throw new Error("Please provide at least one stream key");
        }

        // Validate IP address format
        const ipRegex = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/;
        if (!ipRegex.test(this.formData.ipAddress)) {
          throw new Error("Invalid IP address format");
        }

        // Validate ports
        if (
          this.formData.rtmpPort < 1 ||
          this.formData.rtmpPort > 65535 ||
          this.formData.webrtcPort < 1 ||
          this.formData.webrtcPort > 65535
        ) {
          throw new Error("Ports must be between 1 and 65535");
        }

        const serverData = {
          name: this.formData.name.trim(),
          ipAddress: this.formData.ipAddress.trim(),
          rtmpPort: this.formData.rtmpPort,
          webrtcPort: this.formData.webrtcPort,
          webrtcDomain: this.formData.webrtcDomain.trim() || null,
          streamKeys: availableKeys,
        };

        await createStreamingServer(serverData);
        this.successMessage = "Server created successfully!";

        // Emit success event
        this.$emit("server-created");

        // Close modal after short delay
        setTimeout(() => {
          this.handleClose();
        }, 1500);
      } catch (error) {
        console.error("Error creating server:", error);
        this.errorMessage =
          error.message || "Failed to create server. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.clearForm();
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1.5rem 1.5rem 0 1.5rem;
  color: #111827;
}

.line-divider {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 1rem 1.5rem;
}

.modal-form {
  padding: 0 1.5rem 1.5rem 1.5rem;
}

.error-message {
  color: #ef4444;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
}

.success-message {
  color: #10b981;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
}

.form-group {
  margin-bottom: 1.25rem;
  text-align: left;
}

.form-row {
  display: flex;
  gap: 1rem;
}

.form-group-half {
  flex: 1;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #374151;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  outline: none;
  font-family: inherit;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus {
  border-color: #000;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 80px;
}

.form-hint {
  display: block;
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.button-group {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.modal-submit {
  flex: 1;
  background-color: #000;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s;
}

.modal-submit:hover:not(:disabled) {
  background-color: #1a1a1a;
}

.modal-submit:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.modal-cancel {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background-color: white;
  color: #374151;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
}

.modal-cancel:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.modal-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
  }

  .button-group {
    flex-direction: column;
  }

  .modal-submit,
  .modal-cancel {
    width: 100%;
  }
}
</style>
